import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import WalletButton from '@/components/WalletButton';
import { Lock, Wallet, Trophy, Heart, Star, BarChart2, Menu } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Dashboard',   icon: <Wallet size={15} />,   path: '/dashboard'   },
  { label: 'Leaderboard', icon: <Trophy size={15} />,   path: '/leaderboard' },
  { label: 'Donate',      icon: <Heart size={15} />,    path: '/donate'      },
  { label: 'Prize Pools', icon: <Star size={15} />,     path: '/prize-pools' },
  { label: 'Stats',       icon: <BarChart2 size={15} />, path: '/stats'      },
];

interface NavHeaderProps {
  sticky?: boolean;
}

export const NavHeader: React.FC<NavHeaderProps> = ({ sticky = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const logoBlock = (
    <div className="flex items-center gap-2">
      <div
        className="w-7 h-7 rounded flex items-center justify-center"
        style={{
          background: 'hsl(38 65% 57% / 0.15)',
          border: '1px solid hsl(38 65% 57% / 0.4)',
        }}
      >
        <Lock size={14} style={{ color: 'hsl(38 65% 57%)' }} />
      </div>
      <span
        className="text-lg font-semibold tracking-wide"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 92%)' }}
      >
        SelfWorth
      </span>
    </div>
  );

  return (
    <header
      className={`${sticky ? 'sticky' : 'fixed'} top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4`}
      style={{
        background: 'hsl(30 8% 4% / 0.9)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid hsl(35 12% 14%)',
      }}
    >
      <button onClick={() => navigate('/')} className="focus:outline-none">
        {logoBlock}
      </button>

      <div className="flex items-center gap-3">
        {/* Desktop nav links */}
        {NAV_LINKS.map((link) => {
          const isActive = location.pathname === link.path;
          return (
            <Button
              key={link.path}
              variant="ghost"
              size="sm"
              onClick={() => navigate(link.path)}
              className="hidden md:inline-flex items-center gap-1.5 text-sm"
              style={{
                color: isActive ? 'hsl(38 65% 57%)' : 'hsl(35 8% 52%)',
                background: isActive ? 'hsl(38 65% 57% / 0.08)' : 'transparent',
              }}
            >
              {link.icon}
              {link.label}
            </Button>
          );
        })}

        <WalletButton />

        {/* Hamburger — mobile only */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden p-2"
              style={{ color: 'hsl(35 8% 60%)' }}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-64 flex flex-col gap-0 pt-12"
            style={{
              background: 'hsl(30 8% 5%)',
              borderLeft: '1px solid hsl(35 12% 14%)',
            }}
          >
            <div className="px-4 mb-6">{logoBlock}</div>
            <nav className="flex flex-col gap-1 px-3">
              {NAV_LINKS.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <button
                    key={link.path}
                    onClick={() => { navigate(link.path); setOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 text-left"
                    style={{
                      color: isActive ? 'hsl(38 65% 57%)' : 'hsl(35 8% 60%)',
                      background: isActive ? 'hsl(38 65% 57% / 0.08)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'hsl(35 12% 10%)';
                      (e.currentTarget as HTMLButtonElement).style.color = 'hsl(40 15% 85%)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        isActive ? 'hsl(38 65% 57% / 0.08)' : 'transparent';
                      (e.currentTarget as HTMLButtonElement).style.color =
                        isActive ? 'hsl(38 65% 57%)' : 'hsl(35 8% 60%)';
                    }}
                  >
                    <span style={{ color: 'hsl(38 65% 57%)' }}>{link.icon}</span>
                    {link.label}
                  </button>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default NavHeader;
